/* eslint-disable react/prop-types */
import React, { createRef, useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import _ from 'lodash';
import moment from 'moment';
import { ExpandCollapse } from '@tds/core-expand-collapse';
import Notification from '@tds/core-notification';
import Text from '@tds/core-text';
import Modal from '@tds/community-modal';
import FormField from './formField';
import ValidateLoader from '../UI/validateLoader';
import ProgressBar from '../design/progressBar';
import BrpStatusBar from './BRP/brpStatusBar';
import BrpUserDetailsForm from './BRP/brpUserDetailsForm';
import BrpFileUpload from './BRP/brpFileUpload';
import Note from '../note';
import RepeatedForm from './repeatedForm';
import { msgDisplay, getFieldValue } from './utils';
import { isRelatedFieldExisted } from './formStepWLSUtils';
import { findField } from './formStepUtils';
import { useTranslationsContext } from '../context/TranslationsContext';
import { Image } from '../design/elements';
import { useLocaleContext } from '../context/LocaleContext';
import {
  FormFieldsWrapper,
  BrpGroupWrapper,
  BrpGroupTitle,
  FormStepImageContainer,
  FormStepContainerBRP
} from './css';
import {
  typeCheckbox,
  categoryBrp,
  brpOrderForm,
  brpPrequalification,
  fieldBrpAvailibility,
  fieldBrpProductToBook,
  fieldBrpInsideWiringRequired,
  fieldBrpServiceAddressStreetNumber,
  fieldBrpServiceAddressStreetName,
  fieldBrpServiceAddressUnitNumber,
  fieldBrpServiceAddressCity,
  fieldBrpServiceAddressProvince,
  fieldBrpServiceAddressPostalCode,
  fieldBrpSupportingDocuments
} from '../utils/constant';

const formFieldsGroup = 'formFieldsGroup';
const formSection = 'formSection';
const userDetailsKey = 'userDetails';
const repeatedFormKey = 'repeatedForm';
const typeMultiCheckbox = 'MultiCheckbox';

const FormStepBRP = ({
  stepData,
  steps,
  step,
  updateStep,
  history,
  setFormRef,
  onFormSubmit,
  orderData,
  hideProgressBar,
  isEditing,
  formName
}) => {
  if (!stepData) return null;

  const translations = useTranslationsContext();
  const {
    formButtons: { confirmButtonLabel, cancelButtinLabel },
    contentfulValues: {
      yes,
      valueFixedWirelessInternet,
      valueMicroDpuInternet,
      valueMpo,
      valueFibreGponUnmanagedServices,
      valueFibreInternetOrderBasedDrops,
      valueFibreInternetInsideWiringRequired,
      valueCopperVoiceUnmanagedServices,
      valueCopperHsiaDslUnmanagedServices,
      valueVoiceOverFibreUnmanagedServices,
      valueFibreHsiaDslUnmanagedServices,
      valuePublicWifiUnmanagedServices,
      valueVoiceUnmanagedServices,
      valueHsiaUnmanagedServices,
      valueProductBundleUnmanagedServices,
      valueCpsUnmanagedServices
    },
    formStep: {
      minValueErrorMessage,
      maxValueErrorMessage,
      minLengthErrorMessage,
      maxLengthErrorMessage
    },
    modalForm: { fieldRequiredMessage }
  } = translations;

  const {
    fields: { name: formStepName, image, formStepFields, modalForms }
  } = stepData;

  const [isLoading, setIsLoading] = useState(true);
  const [isEligible, setIsEligible] = useState({ status: false, message: '' });
  const [isFormTouched, setIsFormTouched] = useState(false);
  const [formFields, setFormFields] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [sections, setSections] = useState([]);
  const [modalData, setModalData] = useState({
    title: '',
    body: '',
    confirmText: confirmButtonLabel,
    cancelText: cancelButtinLabel,
    open: false
  });
  const formStepRef = createRef();
  const locale = useLocaleContext();
  const formSections = formStepFields.filter(({ sys }) => {
    const {
      contentType: {
        sys: { id }
      }
    } = sys;

    return id === formSection;
  });

  const getDefaultValue = (key, value) => {
    if (isEditing && orderData) return orderData[key];
    return value || '';
  };

  const getFullAddress = fields =>
    `${_.get(
      fields,
      `${fieldBrpServiceAddressStreetNumber}.value`,
      ''
    )} ${_.get(fields, `${fieldBrpServiceAddressStreetName}.value`, '')} ${_.get(fields, `${fieldBrpServiceAddressUnitNumber}.value`, '')
      ? `Unit ${_.get(
        fields,
        `${fieldBrpServiceAddressUnitNumber}.value`,
        ''
      )}`
      : ''
    } ${_.get(fields, `${fieldBrpServiceAddressCity}.value`, '')} ${_.get(
      fields,
      `${fieldBrpServiceAddressProvince}.value`,
      ''
    )} ${_.get(fields, `${fieldBrpServiceAddressPostalCode}.value`, '')}`;

  const getFormData = fields => {
    const result = Object.keys(fields)
      .filter(
        key => fields[key].isVisible && typeof fields[key].value !== 'undefined'
      )
      .reduce((acc, key) => {
        const { type, value, checked } = fields[key];
        /**
         * Replacing new line char with a comma(,)
         * for multiline text fields
         * */
        if (type === 'MultiLineText')
          acc[key] = value && value.replace(/\n/g, ', ');
        else acc[key] = type === 'Checkbox' ? checked : value;

        return acc;
      }, {});
    /**
     * Assigning data for file fields to attachements key
     */
    if (_.has(result, fieldBrpSupportingDocuments))
      result.attachements = [...result[fieldBrpSupportingDocuments]];
    if (step === 1) result.brpCustomerAddress = getFullAddress(fields);

    return result;
  };

  const normalizedFields = (data, initFields) => {
    let result = initFields;

    if (data) {
      data.forEach(item => {
        const { sys, fields } = item;
        const {
          name,
          type,
          required,
          value,
          minValue,
          maxValue,
          minLength,
          maxLength,
          price,
          monthlyPayment,
          options,
          tooltip,
          noteAppearsBelowField,
          customFieldErrorMessage
        } = fields;
        const {
          contentType: {
            sys: { id }
          }
        } = sys;
        const key = name;
        const ref = createRef();

        if (id === formFieldsGroup) {
          result = normalizedFields(fields.fields, result);
        } else if (id === formSection) {
          result = normalizedFields(fields.formFields, result);
        } else if (id === userDetailsKey) {
          result = normalizedFields(fields.formStepFields, result);
        } else if (id === repeatedFormKey) {
          const formObjectKeys = fields.formFields.reduce(
            (acc, { fields: { name: formNameKey, value: defaultValue } }) => {
              acc[formNameKey] = defaultValue;
              return acc;
            },
            {}
          );
          result[fields.key] = [formObjectKeys];
        } else if (!Object.prototype.hasOwnProperty.call(result, key)) {
          const defaultValue = getDefaultValue(key, value);
          let updatedMonthlyPayment = monthlyPayment;
          let updatedPrice = price;

          if (['Select', 'Radio'].includes(type)) {
            if (options && defaultValue) {
              const currentOption = options.find(
                o => o.fields.value === defaultValue
              );

              if (typeof currentOption !== 'undefined') {
                updatedPrice = currentOption.fields.price;
                updatedMonthlyPayment = currentOption.fields.monthlyPayment;
              }
            }
          }

          if (type === typeMultiCheckbox) {
            if (defaultValue && defaultValue.length > 0 && options) {
              const updateDefaultValue =
                typeof defaultValue === 'string'
                  ? defaultValue.split(',')
                  : defaultValue;
              const mappedValues = updateDefaultValue.reduce(
                (ac, a) => ({ ...ac, [a]: true }),
                {}
              );
              updatedPrice = options.reduce(
                (ac, a) =>
                  ac + ((mappedValues[a.fields.value] && a.fields.price) || 0),
                0
              );
              updatedMonthlyPayment = options.reduce(
                (ac, a) =>
                  ac +
                  ((mappedValues[a.fields.value] && a.fields.monthlyPayment) ||
                    0),
                0
              );
            }
          }

          result[key] = {
            name,
            ref,
            type,
            value: defaultValue || value,
            initPrice: updatedPrice || 0,
            price: updatedPrice || 0,
            initMonthlyPayment: updatedMonthlyPayment || 0,
            monthlyPayment: updatedMonthlyPayment || 0,
            minValue,
            maxValue,
            minLength,
            maxLength,
            isRequired: required || false,
            isVisible: true,
            options,
            tooltip,
            noteAppearsBelowField,
            customFieldErrorMessage
          };

          if (type === 'Checkbox') {
            result[key].checked = defaultValue;
          }

          if (
            type === 'PlusMinusInput' &&
            defaultValue === undefined &&
            value
          ) {
            result[key].value = value;
          }
        }
      });
    }

    return result;
  };

  const getHiddenSections = fields => {
    const hiddenSections = [];

    formSections.forEach(section => {
      const {
        fields: { name: sectionName }
      } = section;
      if (
        !isRelatedFieldExisted(section.fields, fields, orderData, isFormTouched)
      ) {
        hiddenSections.push(sectionName);
      }
    });

    return hiddenSections;
  };

  const updateSections = updatedSections => {
    let newSections = [...formSections];
    updatedSections.forEach(item => {
      newSections = newSections.filter(section => section.fields.name !== item);
    });
    setSections(newSections);
  };

  const sectionIsVisible = name =>
    typeof sections.find(item => item.fields.name === name) !== 'undefined';

  const getRepeatedFormErrors = (contentfulFields, formValues) => {
    const fields = contentfulFields.filter(
      ({ fields: content }) => content.required || content.fieldValidation
    );
    return formValues.filter(formValue => {
      let filteredValues = fields.filter(
        ({ fields: { name: formKeyValue } }, index) => {
          return (
            !formValue[formKeyValue] || fields[index].fields.fieldValidation
          );
        }
      );

      const removeValidationErrors =
        filteredValues &&
        filteredValues.filter(item => {
          if (item.fields.fieldValidation) {
            if (
              item.fields.fieldValidation.fields.logic &&
              item.fields.fieldValidation.fields.logic === 'regex'
            ) {
              const regex = new RegExp(
                item.fields.fieldValidation.fields.value
              );
              if (
                !regex.test(
                  formFields.emergencyContactDetails[0][item.fields.name]
                )
              ) {
                return item;
              }
              if (
                formFields.emergencyContactDetails[0][item.fields.name].length >
                item.fields.maxLength
              ) {
                return item;
              }
            }
          }
          return null;
        });
      filteredValues = removeValidationErrors;
      return filteredValues.length > 0;
    });
  };

  const getErrors = (data, errors) => {
    let result = errors;
    if (!data) return result;
    data.forEach(({ sys, fields }) => {
      const {
        contentType: {
          sys: { id }
        }
      } = sys;
      const { name: key, type, customFieldErrorMessage } = fields;
      const {
        value,
        minValue,
        maxValue,
        isRequired,
        isVisible,
        ref,
        minLength,
        maxLength
      } =
        formFields &&
        Object.prototype.hasOwnProperty.call(formFields, key) &&
        formFields[key];

      if (
        id === formFieldsGroup &&
        isRelatedFieldExisted(fields, formFields, orderData, isFormTouched)
      ) {
        result = getErrors(fields.fields, result);
      } else if (id === formSection && sectionIsVisible(key)) {
        result = getErrors(fields.formFields, result);
      } else if (id === userDetailsKey) {
        result = getErrors(fields.formStepFields, result);
      } else if (id === repeatedFormKey) {
        const repeatedFormErrors = getRepeatedFormErrors(
          fields.formFields,
          formFields[fields.key]
        );
        if (repeatedFormErrors.length) {
          result[fields.key] = repeatedFormErrors;
        } else {
          delete result[fields.key];
        }
      } else if (isVisible) {
        if (isRequired) {
          let fieldValue = value;
          if (type === typeMultiCheckbox) {
            const multiCheckboxValue = getFieldValue(key, formFields);
            fieldValue = multiCheckboxValue && multiCheckboxValue.length > 0;
          }
          if (
            !fieldValue &&
            isRelatedFieldExisted(fields, formFields, orderData, isFormTouched)
          ) {
            if (!(type === typeCheckbox && formFields[key].checked)) {
              result[key] = {
                text: customFieldErrorMessage || fieldRequiredMessage,
                ref
              };
            } else {
              delete result[key];
            }
          } else {
            delete result[key];
          }
        }
        if (typeof minValue !== 'undefined' && parseFloat(value) < minValue) {
          result[key] = {
            text: msgDisplay(minValue, minValueErrorMessage),
            ref
          };
        }
        if (typeof maxValue !== 'undefined' && parseFloat(value) > maxValue) {
          result[key] = {
            text: msgDisplay(maxValue, maxValueErrorMessage),
            ref
          };
        }
        if (minLength && value && value.length < minLength) {
          result[key] = {
            text: msgDisplay(minLength, minLengthErrorMessage),
            ref
          };
        }
        if (maxLength && value && value.length > maxLength) {
          result[key] = {
            text: msgDisplay(maxLength, maxLengthErrorMessage),
            ref
          };
        }

        if (fields.fieldValidation && fields.fieldValidation.fields) {
          if (
            typeof fields.appearsAfterField === 'undefined' ||
            (typeof fields.appearsAfterField !== 'undefined' &&
              isRelatedFieldExisted(
                fields,
                formFields,
                orderData,
                isFormTouched
              ))
          ) {
            const {
              errorMessage,
              logic,
              value: expectedValue
            } = fields.fieldValidation.fields;
            let hasError = false;
            switch (logic) {
              case 'equal':
                // eslint-disable-next-line eqeqeq
                hasError = !expectedValue.split('|').includes(value);
                break;
              case 'regex':
                {
                  const regexLogic = new RegExp(expectedValue);
                  hasError = !regexLogic.test(value);
                }
                break;
              case 'gt':
                hasError = parseInt(value, 10) < parseInt(expectedValue, 10);
                break;
              case 'gte':
                hasError = parseInt(value, 10) <= parseInt(expectedValue, 10);
                break;
              case 'lt':
                hasError = parseInt(value, 10) < parseInt(expectedValue, 10);
                break;
              default:
            }
            if (hasError) result[key] = { text: errorMessage, ref };
            if ((!isRequired && !value) || (!isRequired && !hasError))
              delete result[key];
          }
        }
      }
      if (key === fieldBrpInsideWiringRequired) {
        const valueProvince = _.get(
          orderData,
          fieldBrpServiceAddressProvince,
          ''
        );
        if (!['Alberta', 'British Columbia'].includes(valueProvince))
          delete result[key];
      }
    });

    return result;
  };

  const prevStep = e => {
    e.preventDefault();
    setFormErrors({});
    setIsLoading(true);
    updateStep(step - 1);
    history.push({
      pathname: `/${locale}/${categoryBrp}/${brpOrderForm}`,
      search: `?orderId=${orderData.id}&step=${step - 1}&isEditing=true`
    });
  };

  const nextStep = e => {
    e.preventDefault();
    const errors = getErrors(formStepFields, { ...formErrors });
    setFormErrors(errors);
    if (Object.entries(errors).length === 0) {
      const fields = getFormData(formFields);
      onFormSubmit({ ...fields });
    } else {
      const firstError = errors[Object.keys(errors)[0]];
      const { ref } = firstError;
      if (ref && ref.current) ref.current.focus();
    }
  };

  /**
   * Reset hidden field valus
   */
  const resetFormFields = fields => {
    const updatedFields = Object.keys(fields).reduce((acc, fieldKey) => {
      const currentFormField = findField(formStepFields, fieldKey);
      const isVisible = isRelatedFieldExisted(
        _.get(currentFormField, 'fields', {}),
        fields,
        orderData,
        true
      );

      if (!isVisible)
        return { ...acc, [fieldKey]: { ...fields[fieldKey], value: '' } };
      return { ...acc, [fieldKey]: { ...fields[fieldKey] } };
    }, {});
    return updatedFields;
  };

  const handleFormField = ({ name, value, checked }, ref, isFieldModified) => {
    const fields = { ...formFields };

    if (isFieldModified && !isFormTouched) {
      setIsFormTouched(true);
    }
    fields[name] = {
      ...fields[name],
      value,
      checked,
      isVisible: true
    };

    if (name === fieldBrpAvailibility && value === yes) {
      setIsEligible({ status: true, message: '' });
    } else {
      setIsEligible({
        status: false,
        message:
          'Please be sure to check all available dates in the system and book first available. Once date is secured, please follow the appropriate Do-Sooner or Escalation channels to secure a better due date. '
      });
    }

    updateSections(getHiddenSections(fields));
    setFormFields(resetFormFields(fields));
  };

  const renderFormFields = data =>
    data &&
    data.length > 0 &&
    data.map(({ sys, fields }, indexRoot) => {
      const {
        contentType: {
          sys: { id }
        },
        id: sysId
      } = sys;
      const { name: fieldName, title, label, type, required } = fields;
      const error = formErrors[fieldName];
      const key = `FormField${fieldName}${indexRoot}${sysId}`;
      let fieldLabel = label;
      let result;
      let selectOptions = _.get(fields, 'options', []);
      /**
       * Replacing the XXXX placeholder in the title
       * to a date 2 months from Today
       */
      if (fieldName === fieldBrpAvailibility) {
        fieldLabel = label.replace(
          'XXXX',
          moment(new Date())
            .add('2', 'M')
            .format('MM-DD-YYYY')
        );
      }

      if (fieldName === fieldBrpProductToBook) {
        const valueProvince = _.get(
          orderData,
          fieldBrpServiceAddressProvince,
          ''
        );
        if (!['Alberta', 'British Columbia'].includes(valueProvince)) {
          selectOptions = fields.options.filter(
            item =>
              ![
                valueMpo,
                valueFibreGponUnmanagedServices,
                valueCopperVoiceUnmanagedServices,
                valueCopperHsiaDslUnmanagedServices,
                valueVoiceOverFibreUnmanagedServices,
                valueFibreHsiaDslUnmanagedServices,
                valuePublicWifiUnmanagedServices,
                valueFixedWirelessInternet,
                valueMicroDpuInternet,
                valueFibreInternetInsideWiringRequired,
                valueFibreInternetOrderBasedDrops
              ].includes(item.fields.value)
          );
        }
        if (
          ['Alberta', 'British Columbia', 'Quebec', 'Québec'].includes(
            valueProvince
          )
        ) {
          selectOptions = selectOptions.filter(
            item =>
              ![
                valueVoiceUnmanagedServices,
                valueHsiaUnmanagedServices,
                valueProductBundleUnmanagedServices,
                valueCpsUnmanagedServices
              ].includes(item.fields.value)
          );
        }
      }

      if (fieldName === fieldBrpInsideWiringRequired) {
        const valueProvince = _.get(
          orderData,
          fieldBrpServiceAddressProvince,
          ''
        );
        if (!['Alberta', 'British Columbia'].includes(valueProvince))
          return false;
      }

      if (!isRelatedFieldExisted(fields, formFields, orderData, isFormTouched))
        return null;

      const fieldValue = getFieldValue(fieldName, formFields);
      if (id === formFieldsGroup) {
        const groupIsVisible = fields.fields.find(item => {
          const { fields: itemFields } = item;

          if (!formFields[itemFields.name]) return null;
          const { value, isVisible } = formFields[itemFields.name];
          return (
            (value && isVisible) ||
            isRelatedFieldExisted(
              itemFields,
              formFields,
              orderData,
              isFormTouched
            )
          );
        });

        if (typeof groupIsVisible !== 'undefined') {
          result = (
            <BrpGroupWrapper key={key}>
              {title && <BrpGroupTitle>{title}</BrpGroupTitle>}
              {renderFormFields(fields.fields)}
            </BrpGroupWrapper>
          );
        }
      } else if (id === formSection) {
        const { name, note, isPanelInitiallyOpen } = fields;
        const open = isPanelInitiallyOpen ? [name] : [];

        result = sectionIsVisible(fieldName) && (
          <ExpandCollapse tag="h2" topDivider={false} open={open}>
            <ExpandCollapse.Panel id={fieldName} header={title}>
              <>
                {note && note.content && <Note data={note} type="richText" />}
                {renderFormFields(fields.formFields)}
              </>
            </ExpandCollapse.Panel>
          </ExpandCollapse>
        );
      } else if (id === userDetailsKey) {
        result = (
          <BrpUserDetailsForm
            key={key}
            data={fields}
            setFormFields={setFormFields}
            orderData={orderData}
            formErrors={formErrors}
            formFields={formFields}
            isEditing={isEditing}
            isFormTouched={isFormTouched}
            setIsFormTouched={setIsFormTouched}
          />
        );
      } else if (id === repeatedFormKey) {
        result = (
          <RepeatedForm
            key={key}
            data={fields}
            setFormFields={setFormFields}
            orderData={orderData}
            formErrors={formErrors}
            formFields={formFields}
            isEditing={isEditing}
            isFormTouched={isFormTouched}
            setIsFormTouched={setIsFormTouched}
          />
        );
      } else if (id === 'note') {
        const dataObj = {
          type: 'TextNote',
          note: fields.content,
          isShowNoteBackground: true
        };
        result = <FormField data={dataObj} />;
      } else if (type === 'File') {
        const dataObj = {
          label: fieldLabel,
          name: fieldName,
          required,
          value: fieldValue,
          formErrors,
          onChange: (e, refV) => handleFormField(e.target, refV, true)
        };
        result = <BrpFileUpload data={dataObj} />;
      } else {
        const fieldButtonProps = fields.type === 'Button' ? { modalForms } : {};
        let ref;
        let isDisabled;
        if (formFields[fieldName]) {
          ({ ref, isDisabled } = formFields[fieldName]);
        }

        const fieldData = {
          ...fields,
          label: fieldLabel,
          ...fieldButtonProps,
          options: selectOptions,
          ref,
          formFields,
          name: fieldName,
          value: fieldValue,
          disabled: isDisabled,
          readonly: isDisabled,
          sysId,
          onChange: (e, refV) => handleFormField(e.target, refV, true),
          onBlur: () => { }
        };

        result = (
          <FormField key={key} data={fieldData} error={error && error.text} />
        );
      }
      return result;
    });

  useEffect(() => {
    if (
      (!isEditing && formStepFields) ||
      (isEditing && orderData && orderData.id)
    ) {
      const updatedFields = normalizedFields(formStepFields, formFields);
      setFormFields(updatedFields);
      updateSections(getHiddenSections(updatedFields));
      setIsLoading(false);
      setIsFormTouched(false);
      if (formStepRef && formStepRef.current)
        formStepRef.current.scrollTo(0, 0);
    }
  }, [formStepFields, orderData, locale]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  // Show footer only on landing page (not the form steps).
  useEffect(() => {
    if (formName === brpOrderForm) {
      document.getElementById('footer').style.display = 'none';
    } else {
      document.getElementById('footer').style.display = 'block';
    }
  }, [formName]);

  useEffect(() => {
    if (isEditing && orderData && orderData.id) {
      const updatedFields = normalizedFields(formStepFields, formFields);
      setFormFields(updatedFields);
    }
  }, [orderData, formStepFields]);

  return (
    <div ref={formStepRef}>
      {!hideProgressBar && (
        <ProgressBar
          name={formStepName}
          steps={steps}
          currentStep={step}
          locale={locale}
        />
      )}
      {isLoading ? (
        <ValidateLoader
          message="Please wait request is In progress...."
          isFormStep
        />
      ) : (
        <div style={{ backgroundColor: '#fff' }}>
          <FormStepContainerBRP>
            <div id="main">
              <div id="main-wrapper">
                <div id="main-content">
                  <form
                    name={formStepName}
                    onSubmit={e => nextStep(e)}
                    ref={ref => setFormRef && setFormRef(ref)}
                  >
                    <FormFieldsWrapper
                      style={{
                        marginBottom:
                          formName === brpOrderForm ? '10rem' : '2rem'
                      }}
                    >
                      <div style={{ width: '89%' }}>
                        {formStepFields && renderFormFields(formStepFields)}
                      </div>
                      {image && (
                        <FormStepImageContainer>
                          <Image image={image} name={formStepName} />
                        </FormStepImageContainer>
                      )}
                    </FormFieldsWrapper>
                  </form>
                </div>
              </div>
            </div>
            {formName === brpPrequalification && (
              <div
                className="container 85%"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  marginTop: '-1rem'
                }}
              >
                {isEligible.message && (
                  <Notification variant="error" copy="en">
                    <Text bold>{isEligible.message}</Text>
                  </Notification>
                )}

                <button
                  type="button"
                  onClick={nextStep}
                  className={`frg-button ${isEligible.status ? 'color-green' : 'state-disabled'
                    }`}
                  style={{ margin: '1em 0', width: '18%' }}
                >
                  New Request
                </button>

              </div>
            )}
          </FormStepContainerBRP>
        </div>
      )}
      {formName === brpOrderForm && (
        <BrpStatusBar
          onProceedClick={nextStep}
          onBackClick={prevStep}
          step={step}
          isLastStep={steps.length === step}
        />
      )}
      {
        <Modal
          heading={modalData.title}
          bodyText={modalData.body}
          confirmCTAText={modalData.confirmText}
          cancelCTAText={modalData.cancelText}
          onConfirm={() =>
            history.push({
              pathname: '/'
            })
          }
          onClose={() => setModalData({ ...modalData, open: false })}
          isOpen={modalData.open}
          focusElementAfterClose={formStepRef}
        />
      }
    </div>
  );
};

FormStepBRP.propTypes = {
  stepData: PropTypes.shape({
    fields: PropTypes.shape({
      name: PropTypes.string,
      image: PropTypes.string,
      formStepFields: PropTypes.arrayOf(PropTypes.shape({})),
      formSummary: PropTypes.shape({
        fields: PropTypes.objectOf(PropTypes.shape({}))
      }),
      showTotal: PropTypes.bool,
      modalForms: PropTypes.arrayOf(PropTypes.shape({})),
      backendKey: PropTypes.string
    })
  }),
  steps: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  step: PropTypes.number.isRequired,
  updateStep: PropTypes.func,
  setFormRef: PropTypes.func,
  onFormSubmit: PropTypes.func,
  orderData: PropTypes.objectOf(PropTypes.shape({})),
  hideProgressBar: PropTypes.bool,
  isEditing: PropTypes.string.isRequired,
  formName: PropTypes.string.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func
  }).isRequired
};

FormStepBRP.defaultProps = {
  stepData: null,
  updateStep: () => { },
  setFormRef: null,
  onFormSubmit: null,
  orderData: {},
  hideProgressBar: false
};

export default FormStepBRP;
